import { useState, useEffect } from "react";
import { useMutation, useQueryCache } from "react-query";
import { useCookies } from "react-cookie";
import Loader from "./Loader";
import { useNavigate } from "react-router";
import { usePlace } from "../api/queries";
import { updatePlaceMenuOrder } from "../api/mutations";
import PreviewButton from "./PreviewButton";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { getPreviewUrl } from "../utils/queryUtils";
import MenuHeader from "./MenuHeader";
import MenuHeading from "./MenuHeading";
import MenuForm from "./MenuForm";
import ButtonGroup from "./ButtonGroup";
import NextText from "./NextText";
import Button from "./Button";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import forEach from "lodash/forEach";
import { trackEventSegment } from "../utils/segmentUtils";
import MenuListItem from "./MenuListItem";
import AddButton from "./AddButton";
import Swal from "sweetalert2";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  forEach(result, (item, index) => {
    item["order"] = index + 1;
  });

  return result;
};

const MenuCreate = (props) => {
  const [cookies, setCookie] = useCookies(["placeId"]);
  const [loaded, setLoaded] = useState(false);

  let navigate = useNavigate();

  const [addItemExpanded, setAddItemExpanded] = useState(false);

  const cache = useQueryCache();

  const [menus, setMenus] = useState([]);

  const onMutate = (data) => {
    const updatedMenus = data.values.menus;
    cache.cancelQueries("placeDetail");
    const previousPlace = cache.getQueryData(["placeDetail"]);
    const newPlace = Object.assign({}, previousPlace);
    newPlace.menus = updatedMenus;
    cache.setQueryData(["placeDetail"], newPlace);
    return () => cache.setQueryData("placeDetail", previousPlace);
  };

  const [mutate] = useMutation(updatePlaceMenuOrder, { onMutate });

  const { data } = usePlace(cookies.placeId);

  const [activeMenu, setActiveMenu] = useState(null);

  useEffect(() => {
    if (data && loaded === false) {
      var menus = data.menus;
      if (menus.length === 0) {
        setAddItemExpanded(true);
      }
      setMenus(menus);
    } else if (data && loaded === true) {
      setMenus(data.menus);
    }
    if (data) {
      setLoaded(true);
    }
  }, [data]);

  const addMenu = (menu) => {
    setAddItemExpanded(false);
    setMenus((menus) => [...menus, menu]);
  };

  const changedMenu = () => {
    setAddItemExpanded(false);
    setActiveMenu(null);
  };

  const editMenu = (menu) => {
    changedMenu();
    setActiveMenu(menu);
    setAddItemExpanded(true);
  };

  const removeMenu = async (menu) => {
    const newList = menus.filter((item) => item.uuid !== menu);
    setMenus(newList);
    await mutate({
      menuId: cookies.menuId,
      values: {
        menus: newList,
      },
    });
  };

  const onDragEnd = (result) => {
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }
    const items = reorder(menus, result.source.index, result.destination.index);
    setMenus(items);
    //var destDish = find(categories, ['order', result.destination.index + 1])
    setLoaded(false);
    mutate(
      {
        placeId: cookies.placeId,
        values: {
          menus: menus,
        },
      },
      {
        onSuccess: () => {
          trackEventSegment("Reordered menus", {
            placeId: cookies.placeId,
          });
        },
        onSettled: () => {
          cache.invalidateQueries("placeDetail");
        },
        onError: (error, menus, rollback) => {
          rollback();
        },
      }
    );
  };

  const setPrev = () => {
    navigate("/menu-builder/visual-menu-choice");
  };

  const setNext = () => {
    if (menus.length > 0) {
      navigate("/menu-builder/visual-menu/create-items");
    } else {
      Swal.fire({
        title: "Create your menu first",
        text: "Create your first menu before proceeding to this step!",
        confirmButtonText: "Got it",
      });
    }
  };

  const list = () => {
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="list">
          {(provided, snapshot) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {menus.map((item, index) => (
                <Draggable
                  key={item.uuid}
                  draggableId={item.uuid}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <MenuListItem
                        removeMenu={removeMenu}
                        addMenu={addMenu}
                        editMenu={editMenu}
                        menu={item}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  return (
    <>
      <MenuHeader>
        <MenuHeading>Create Menu</MenuHeading>
        <PreviewButton title="Preview" icon={faEye} small={true} href={getPreviewUrl(cookies.placeId)} />
      </MenuHeader>
      {loaded ? (
        <>
          {addItemExpanded && (
            <MenuForm numOfMenus={menus.length} addNewMenu={addMenu} menu={activeMenu} changedMenu={changedMenu} />
          )}
          {menus.length > 0 && list()}
          {!addItemExpanded && (
            <AddButton onClick={() => setAddItemExpanded(true)} type="button">
              Add new menu
            </AddButton>
          )}
          <ButtonGroup halfWidth={true}>
            <Button type="button" onClick={() => setPrev()} inversed={true} text="Back" />
            <Button text="Continue" onClick={() => setNext()} type="button" />
          </ButtonGroup>
        </>
      ) : (
        <Loader />
      )}
      <NextText>Next step: Add menu sections</NextText>
    </>
  );
};

export default MenuCreate;
